import { useWindowWidth } from '@liveauctioneers/hammer-ui-core/providers';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useEffect } from 'react';
import useMeasure from 'react-use-measure';

import CategoryModule from '@/components/CategoryModule/CategoryModule';
import ComingUpCarousel from '@/components/ComingUp/ComingUpCarousel';
import CompleteAccountBanner from '@/components/CompleteAccountBanner/CompleteAccountBanner';
import FeaturedAuctioneers from '@/components/FeaturedAuctioneers/FeaturedAuctioneers';
import FeaturedCatalogCarousel from '@/components/FeaturedCatalogCarousel/FeaturedCatalogCarousel';
import FollowedSearchCarousel from '@/components/FollowedSearchCarousel/FollowedSearchCarousel';
import HeroBanner from '@/components/Hero/HeroBanner';
import RecentItemsCarousel from '@/components/RecentItems/RecentItemsCarousel';
import RecommendedItemCarousel from '@/components/RecommendedItem/RecommendedItemCarousel';
import SearchFollowSuggestions from '@/components/SearchFollowSuggestions/SearchFollowSuggestions';
import TrendingItemsCarousel from '@/components/TrendingItems/TrendingItemsCarousel';
import AuctionsYouMayLikeCarousel from '@/components/YouMayLikeCarousel/AuctionsYouMayLikeCarousel';
import { usePrevious } from '@/hooks/usePrevious';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { fetchAllHomeData } from '@/redux/modules/home/home.actions';
import { getFeaturedAuctioneersExist } from '@/redux/modules/home/home.selectors';
import { useVariantJumpingHomePageCheckExperiment } from '@/utils/amplitude/experiments/hooks/useVariantJumpingHomePageCheckExperiment.experiment';

import HomePageHeaderMeta from './HomePageHeaderMeta';
import HomePageUpcomingCatalogs from './HomePageUpcomingCatalogs';

const sharedSectionClasses = 'mdMax:w-[calc(100%-40px)] w-full mx-auto my-sm max-w-[1200px]';
const sectionClasses = 'w-[calc(100%-40px)] max-w-[1200px] mx-auto my-sm';

const HomePage = () => {
    const dispatch = useAppDispatch();
    const isLoggedIn = useAppSelector(isUserLoggedIn);
    const wasLoggedOut = usePrevious(!isLoggedIn);
    const featuredAuctioners = useAppSelector(getFeaturedAuctioneersExist);
    const initialWidth = useWindowWidth();
    const [featuredCatalogsRef, { width }] = useMeasure();
    useEffect(() => {
        // if you were logged out when you landed on this page, we need to reload the data
        if (wasLoggedOut && isLoggedIn) {
            dispatch(fetchAllHomeData());
        }
    }, [isLoggedIn, dispatch, wasLoggedOut]);

    // Mainly for testing variant jumping on home page
    useVariantJumpingHomePageCheckExperiment();

    return (
        <section className="relative flex w-full flex-col content-center">
            <HomePageHeaderMeta />
            <CompleteAccountBanner className="mx-auto mt-md w-[calc(100%-40px)] max-w-[1200px]" />
            {!isLoggedIn && <HeroBanner />}
            <div
                className={cn(sharedSectionClasses, 'mb-md')}
                ref={featuredCatalogsRef}
            >
                <FeaturedCatalogCarousel width={width || initialWidth} />
            </div>
            {isLoggedIn && (
                <ComingUpCarousel
                    className={sharedSectionClasses}
                    testid="comingUpForAuctions"
                />
            )}
            {featuredAuctioners && <FeaturedAuctioneers />}
            {isLoggedIn && <FollowedSearchCarousel className={sectionClasses} />}
            {isLoggedIn && <RecommendedItemCarousel className={sharedSectionClasses} />}
            {isLoggedIn && <AuctionsYouMayLikeCarousel className={sharedSectionClasses} />}
            <SearchFollowSuggestions className={sharedSectionClasses} />
            <RecentItemsCarousel className={sharedSectionClasses} />
            <CategoryModule
                className={cn(sharedSectionClasses, sectionClasses)}
                testid="categoryModule"
            />
            <TrendingItemsCarousel className={sharedSectionClasses} />
            <HomePageUpcomingCatalogs className={sharedSectionClasses} />
        </section>
    );
};

export default HomePage;
